import { ABILITIES, CHARACTER_CLASSES, PATHFINDER_SAVES } from "../catalogs/characterSystemCatalog";
import { CLASS_FEATURE_GRANTS, getCharacterFeature, getClassFeature } from "../catalogs/characterFeaturesCatalog";
import { baseAttackAtLevel, getClassRules, saveAtLevel, spellsPerDayAtLevel } from "../catalogs/classRulesCatalog";
import { CORE_FEATS } from "../catalogs/featsCatalog";
import { getRaceRules } from "../catalogs/raceRulesCatalog";

const number = (value) => Number(value) || 0;
const normalizeChoices = (choices) => Array.isArray(choices)
  ? choices.filter((choice) => choice && choice.id).map((choice) => ({ ...choice }))
  : [];

const generatedFeature = ({ definition, origin, sourceId, sourceLevel = 0, occurrence = 0, value = null, stableSourceLevel = sourceLevel }) => ({
  id: `generated:${origin}:${sourceId}:${definition.id}:${stableSourceLevel}:${occurrence}`,
  featureId: definition.id,
  origin,
  sourceId,
  sourceLevel,
  occurrence,
  value,
  generated: true,
  readOnly: true,
});

const addChoice = (activeChoices, previousById, definition, origin, sourceId, sourceLevel = 0) => {
  const previous = previousById.get(definition.id);
  const choice = { ...previous, ...definition, origin, sourceId, sourceLevel, active: true };
  activeChoices.push(choice);
  return choice;
};

const isFeatChoice = (effect) => effect.type === "choice" && typeof effect.choiceType === "string" && effect.choiceType.includes("feat");
const isGrantedFeat = (effect) => effect.type === "bonus-feat";
const FEAT_IDS = CORE_FEATS.map((feat) => feat.id);
const ABILITY_IDS = ABILITIES.map((ability) => ability.id);
const CLASS_IDS = CHARACTER_CLASSES.map((characterClass) => characterClass.id);
const defaultSkillChoiceOptions = ["craft", "profession"];
const PERFORMANCE_OPTIONS = ["act", "comedy", "dance", "keyboard", "oratory", "percussion", "sing", "string", "wind"];
const CLERIC_DOMAIN_OPTIONS = [
  "air", "animal", "artifice", "chaos", "charm", "community", "darkness", "death", "destruction",
  "earth", "evil", "fire", "glory", "good", "healing", "knowledge", "law", "liberation", "luck",
  "madness", "magic", "nobility", "plant", "protection", "repose", "rune", "strength", "sun",
  "travel", "trickery", "war", "water", "weather",
];
const NATURE_BOND_OPTIONS = ["animal-companion", "domain"];
const WEAPON_GROUP_OPTIONS = [
  "axes", "bows", "close", "crossbows", "double", "firearms", "flails", "hammers",
  "heavy-blades", "light-blades", "monk", "natural", "polearms", "spears", "thrown",
];
const DIVINE_BOND_OPTIONS = ["bonded-weapon", "mount"];
const MERCY_OPTIONS = [
  "fatigued", "shaken", "sickened", "dazed", "diseased", "staggered",
  "cursed", "exhausted", "frightened", "nauseated", "poisoned", "blinded",
  "deafened", "paralyzed", "stunned",
];
const FAVORED_ENEMY_OPTIONS = [
  "aberration", "animal", "construct", "dragon", "fey", "humanoid-aquatic", "humanoid-dwarf",
  "humanoid-elf", "humanoid-giant", "humanoid-goblinoid", "humanoid-gnoll", "humanoid-gnome",
  "humanoid-halfling", "humanoid-human", "humanoid-orc", "magical-beast", "monstrous-humanoid",
  "ooze", "outsider-evil", "outsider-lawful", "outsider-chaotic", "outsider-good", "plant",
  "undead", "vermin",
];
const COMBAT_STYLE_OPTIONS = ["archery", "crossbow", "natural-weapon", "two-weapon", "weapon-and-shield"];
const FAVORED_TERRAIN_OPTIONS = [
  "cold", "desert", "forest", "jungle", "mountain", "plains", "planes", "swamp", "underground", "urban", "water",
];
const HUNTERS_BOND_OPTIONS = ["companions", "party"];
const SORCERER_BLOODLINE_OPTIONS = [
  "aberrant", "abyssal", "arcane", "celestial", "destined", "draconic", "elemental", "fey",
  "infernal", "serpentine", "undead",
];
const ARCANE_BOND_OPTIONS = ["familiar", "bonded-object"];
const ARCANE_SCHOOL_OPTIONS = [
  "abjuration", "conjuration", "divination", "enchantment", "evocation",
  "illusion", "necromancy", "transmutation", "universalist",
];
const ROGUE_TALENT_OPTIONS = [
  "bleeding-attack", "camouflage", "combat-trick", "edge", "fast-stealth",
  "finesse-rogue", "grit", "hard-to-fool", "intimidating-glare", "ki-pool",
  "minor-magic", "ninja-trick", "resiliency", "rogue-crawl", "slow-reactions",
  "stand-up", "surprise-attack", "terrain-mastery", "trap-spotter", "weapon-training",
];
const ADVANCED_ROGUE_TALENT_OPTIONS = [
  "crippling-strike", "defensive-roll", "feat", "hide-in-plain-sight",
  "improved-evasion", "opportunist", "skill-mastery", "slippery-mind",
  "talent", "unwitting-ally",
];
const ADVANCED_ROGUE_TALENT_OPTION_SET = new Set(ADVANCED_ROGUE_TALENT_OPTIONS);
export const isAdvancedRogueTalentOption = (option) => ADVANCED_ROGUE_TALENT_OPTION_SET.has(option);
const LEVEL_ABILITY_INCREASE_LEVELS = [4, 8, 12, 16, 20];

const CHOICE_OPTIONS_BY_TYPE = {
  ability: ABILITY_IDS,
  "arcane-bond": ARCANE_BOND_OPTIONS,
  "arcane-school": ARCANE_SCHOOL_OPTIONS,
  "advanced-rogue-talent": ADVANCED_ROGUE_TALENT_OPTIONS,
  "cleric-domain": CLERIC_DOMAIN_OPTIONS,
  "combat-style": COMBAT_STYLE_OPTIONS,
  "divine-bond": DIVINE_BOND_OPTIONS,
  mercy: MERCY_OPTIONS,
  "favored-class": CLASS_IDS,
  "favored-enemy": FAVORED_ENEMY_OPTIONS,
  "favored-terrain": FAVORED_TERRAIN_OPTIONS,
  "hunters-bond": HUNTERS_BOND_OPTIONS,
  "nature-bond": NATURE_BOND_OPTIONS,
  performance: PERFORMANCE_OPTIONS,
  "rogue-talent": [...ROGUE_TALENT_OPTIONS, ...ADVANCED_ROGUE_TALENT_OPTIONS],
  skill: defaultSkillChoiceOptions,
  "sorcerer-bloodline": SORCERER_BLOODLINE_OPTIONS,
  "weapon-group": WEAPON_GROUP_OPTIONS,
};

const isGeneratedFeatRecord = (feat) => feat?.generated === true || String(feat?.id || "").startsWith("granted:");

const addGeneratedFeat = (grants, featId, origin, sourceId, featureId, occurrence = 0) => {
  if (!featId) return;
  const sources = grants.get(featId) || [];
  const source = { origin, sourceId, featureId, occurrence };
  if (!sources.some((entry) => JSON.stringify(entry) === JSON.stringify(source))) sources.push(source);
  grants.set(featId, sources);
};

const choiceOptionsForEffect = (effect) => {
  if (Array.isArray(effect.options) && effect.options.length > 0) return effect.options;
  if (effect.choiceType === "feat") return FEAT_IDS;
  return CHOICE_OPTIONS_BY_TYPE[effect.choiceType] || [];
};

const applyChoiceEffect = (choice, effect, abilityAdjustments, skillBonuses, featGrants, origin, sourceId, featureId) => {
  if (!choice?.selectedValue) return;
  const choiceType = effect.choiceType || effect.type;
  if (choiceType === "ability" && choice.options.includes(choice.selectedValue)) {
    abilityAdjustments[choice.selectedValue] += number(effect.value ?? 2);
    return;
  }
  if (choiceType === "skill" && choice.options.includes(choice.selectedValue)) {
    skillBonuses[choice.selectedValue] = number(skillBonuses[choice.selectedValue]) + number(effect.value ?? 2);
    return;
  }
  if (choiceType === "feat" && choice.options.includes(choice.selectedValue)) {
    addGeneratedFeat(featGrants, choice.selectedValue, origin, sourceId, featureId, choice.occurrence || 0);
  }
};

export function reconcileCharacterRules(inputCharacter) {
  if (!inputCharacter) return inputCharacter;
  const character = {
    ...inputCharacter,
    race: { ...(inputCharacter.race || {}) },
    characterClass: (inputCharacter.characterClass || []).map((entry) => ({ ...entry })),
    level: { ...(inputCharacter.level || {}) },
    characteristics: { ...(inputCharacter.characteristics || {}) },
    skills: { ...(inputCharacter.skills || {}) },
    feats: Array.isArray(inputCharacter.feats)
      ? inputCharacter.feats.filter((entry) => !isGeneratedFeatRecord(entry)).map((entry) => ({ ...entry }))
      : [],
  };
  const previousChoices = normalizeChoices(inputCharacter.ruleChoices);
  const previousById = new Map(previousChoices.map((choice) => [choice.id, choice]));
  const activeChoices = [];
  const grantedFeatures = [];
  const abilityAdjustments = Object.fromEntries(ABILITIES.map((ability) => [ability.id, 0]));
  const baseSaveBonuses = Object.fromEntries(PATHFINDER_SAVES.map((save) => [save.id, 0]));
  const racialSaveBonuses = Object.fromEntries(PATHFINDER_SAVES.map((save) => [save.id, 0]));
  const skillBonuses = {};
  const untrainedSkillGroups = new Set();
  const skillGroupBonuses = {};
  const featGrants = new Map();
  const dynamicSaveEffects = [];
  const classSkillIds = new Set();
  const weaponProficiencies = new Set();
  const armorProficiencies = new Set();
  const hitDice = [];
  const spellcasting = [];
  let classBaseAttackBonus = 0;
  let skillRanksPerLevelBonus = 0;
  let bonusFeatCapacity = 0;

  const storedClassLevelTotal = character.characterClass.reduce(
    (total, entry) => total + Math.max(0, Math.min(20, Math.trunc(number(entry.classLevel)))),
    0
  );
  const storedCharacterLevel = Math.max(0, Math.min(20, Math.trunc(number(character.level.level))));
  character.level.level = storedCharacterLevel || storedClassLevelTotal;
  character.characterClass = character.characterClass
    .map((entry, index) => ({
      ...entry,
      classLevel: character.characterClass.length === 1
        ? character.level.level
        : Math.max(0, Math.min(20, Math.trunc(number(entry.classLevel)))),
      originalIndex: index,
    }))
    .sort((left, right) => right.classLevel - left.classLevel || left.originalIndex - right.originalIndex)
    .map(({ originalIndex, ...entry }) => entry);
  const classLevelTotal = character.characterClass.reduce(
    (total, entry) => total + entry.classLevel,
    0
  );

  LEVEL_ABILITY_INCREASE_LEVELS.forEach((sourceLevel, occurrence) => {
    if (sourceLevel > character.level.level) return;
    const definition = getCharacterFeature("progression-level-ability-increase");
    if (!definition) return;
    grantedFeatures.push(generatedFeature({
      definition,
      origin: "progression",
      sourceId: "character-level",
      sourceLevel,
      occurrence,
    }));
    (definition.effects || []).forEach((effect, effectIndex) => {
      if (effect.type !== "choice") return;
      const choice = addChoice(activeChoices, previousById, {
        id: `progression:character-level:${definition.id}:${sourceLevel}:${occurrence}:${effectIndex}:0`,
        type: effect.choiceType,
        required: true,
        options: choiceOptionsForEffect(effect),
      }, "progression", "character-level", sourceLevel);
      choice.occurrence = occurrence;
      choice.choiceIndex = 0;
      applyChoiceEffect(choice, effect, abilityAdjustments, skillBonuses, featGrants, "progression", "character-level", definition.id);
    });
  });

  const raceRules = getRaceRules(character.race.id);
  if (raceRules) {
    Object.entries(raceRules.abilityAdjustments || {}).forEach(([abilityId, value]) => {
      abilityAdjustments[abilityId] += value;
    });
    (raceRules.choices || []).forEach((choiceDefinition) => {
      const choice = addChoice(activeChoices, previousById, choiceDefinition, "race", raceRules.id);
      applyChoiceEffect(choice, choiceDefinition, abilityAdjustments, skillBonuses, featGrants, "race", raceRules.id, choiceDefinition.id);
    });
    (raceRules.features || []).forEach((definition, occurrence) => {
      grantedFeatures.push(generatedFeature({ definition, origin: "race", sourceId: raceRules.id, occurrence }));
      (definition.effects || []).forEach((effect) => {
        if (effect.type === "skill-ranks-per-level") skillRanksPerLevelBonus += number(effect.value);
        if (effect.type === "skill-bonus") skillBonuses[effect.skillId] = number(skillBonuses[effect.skillId]) + number(effect.value);
        if (effect.type === "save-bonus") effect.saves.forEach((saveId) => { racialSaveBonuses[saveId] += number(effect.value); });
        if (effect.type === "proficiency" && effect.group === "weapon") effect.values.forEach((value) => weaponProficiencies.add(value));
        if (isFeatChoice(effect) || isGrantedFeat(effect)) bonusFeatCapacity += effect.count || 1;
        if (effect.type === "choice") {
          const count = Math.max(1, Number(effect.count) || 1);
          for (let choiceIndex = 0; choiceIndex < count; choiceIndex += 1) {
            const choice = addChoice(activeChoices, previousById, {
              id: `race:${raceRules.id}:${definition.id}:${occurrence}:${choiceIndex}`,
              type: effect.choiceType,
              required: true,
              options: choiceOptionsForEffect(effect),
            }, "race", raceRules.id);
            choice.occurrence = occurrence;
            choice.choiceIndex = choiceIndex;
            applyChoiceEffect(choice, effect, abilityAdjustments, skillBonuses, featGrants, "race", raceRules.id, definition.id);
          }
        }
        if (effect.type === "bonus-feat") {
          addGeneratedFeat(featGrants, effect.featId, "race", raceRules.id, definition.id, occurrence);
        }
      });
    });
    character.race = {
      ...character.race,
      size: raceRules.size,
      baseSpeed: raceRules.baseSpeed,
      vision: raceRules.vision,
      languages: [...raceRules.languages],
    };
  }

  character.characterClass.forEach((classEntry) => {
    const classRules = getClassRules(classEntry.id);
    const level = classEntry.classLevel;
    classEntry.classLevel = level;
    if (!classRules || level === 0) return;
    classBaseAttackBonus += baseAttackAtLevel(classRules.bab, level);
    PATHFINDER_SAVES.forEach((save) => {
      baseSaveBonuses[save.id] += saveAtLevel(classRules.goodSaves.includes(save.id), level);
    });
    classRules.classSkills.forEach((skillId) => classSkillIds.add(skillId));
    classRules.proficiencies.weapons.forEach((value) => weaponProficiencies.add(value));
    classRules.proficiencies.armor.forEach((value) => armorProficiencies.add(value));
    hitDice.push({ classId: classRules.id, die: classRules.hitDie, count: level });
    if (classRules.spellcasting && level >= (classRules.spellcasting.startsAt || 1)) {
      spellcasting.push({
        classId: classRules.id,
        classLevel: level,
        ...classRules.spellcasting,
        spellsPerDay: spellsPerDayAtLevel(classRules.id, level),
      });
    }

    const occurrences = {};
    (CLASS_FEATURE_GRANTS[classRules.id] || []).forEach(([sourceLevel, shortFeatureId, value]) => {
      if (sourceLevel > level) return;
      const definition = getClassFeature(classRules.id, shortFeatureId);
      if (!definition) return;
      const occurrence = occurrences[definition.id] || 0;
      occurrences[definition.id] = occurrence + 1;
      const record = generatedFeature({
        definition,
        origin: "class",
        sourceId: classRules.id,
        sourceLevel,
        occurrence: definition.stackMode === "replace" ? 0 : occurrence,
        value,
        stableSourceLevel: definition.stackMode === "replace" ? 0 : sourceLevel,
      });
      if (definition.stackMode === "replace") {
        const previousIndex = grantedFeatures.findIndex((feature) => feature.origin === "class"
          && feature.sourceId === classRules.id && feature.featureId === definition.id);
        if (previousIndex >= 0) grantedFeatures[previousIndex] = record;
        else grantedFeatures.push(record);
      } else if (definition.stackMode === "single") {
        const alreadyGranted = grantedFeatures.some((feature) => feature.origin === "class"
          && feature.sourceId === classRules.id && feature.featureId === definition.id);
        if (!alreadyGranted) grantedFeatures.push(record);
      } else {
        grantedFeatures.push(record);
      }
      (definition.effects || []).forEach((effect, effectIndex) => {
        if (effect.type === "choice") {
          if (definition.stackMode !== "repeat" && occurrence > 0) return;
          const count = Math.max(1, Number(effect.count) || 1);
          for (let choiceIndex = 0; choiceIndex < count; choiceIndex += 1) {
            const choice = addChoice(activeChoices, previousById, {
              id: `class:${classRules.id}:${definition.id}:${sourceLevel}:${occurrence}:${effectIndex}:${choiceIndex}`,
              type: effect.choiceType,
              count,
              required: true,
              options: choiceOptionsForEffect(effect),
            }, "class", classRules.id, sourceLevel);
            choice.occurrence = occurrence;
            choice.choiceIndex = choiceIndex;
            applyChoiceEffect(choice, effect, abilityAdjustments, skillBonuses, featGrants, "class", classRules.id, definition.id);
          }
        }
        if (effect.type === "save-bonus" && effect.valueFrom === "ability-modifier") {
          dynamicSaveEffects.push({ effect, featureId: definition.id, sourceId: classRules.id });
        }
        if (effect.type === "skill-bonus" && effect.skillGroup) {
          const resolvedValue = effect.valueFrom === "half-class-level"
            ? Math.max(number(effect.minimum), Math.floor(level / 2))
            : number(effect.value);
          skillGroupBonuses[effect.skillGroup] = number(skillGroupBonuses[effect.skillGroup]) + resolvedValue;
          if (effect.allowsUntrained) untrainedSkillGroups.add(effect.skillGroup);
        }
        if (effect.type === "bonus-feat") {
          if (definition.stackMode !== "repeat" && occurrence > 0) return;
          addGeneratedFeat(featGrants, effect.featId, "class", classRules.id, definition.id, occurrence);
        }
        if (isFeatChoice(effect) || isGrantedFeat(effect)) {
          if (definition.stackMode !== "repeat" && occurrence > 0) return;
          bonusFeatCapacity += effect.count || 1;
        }
      });
    });
  });

  const inactiveChoices = previousChoices
    .filter((choice) => !activeChoices.some((active) => active.id === choice.id))
    .map((choice) => ({ ...choice, active: false }));
  const abilityList = ABILITIES.map((ability) => {
    const existing = (character.characteristics.mainCharacteristicList || []).find((entry) => entry.abilityId === ability.id) || {};
    const baseDiceValue = Number.isFinite(Number(existing.baseDiceValue))
      ? Number(existing.baseDiceValue)
      : (Number.isFinite(Number(existing.diceValue)) ? Number(existing.diceValue) : 10);
    const manualAdjustment = number(existing.manualAdjustment);
    const racialAdjustment = number(abilityAdjustments[ability.id]);
    return {
      ...existing,
      abilityId: ability.id,
      baseDiceValue,
      manualAdjustment,
      racialAdjustment,
      ruleAdjustment: racialAdjustment,
      diceValue: baseDiceValue + manualAdjustment + racialAdjustment,
    };
  });
  character.characteristics.mainCharacteristicList = abilityList;
  const classFeatureSaveBonuses = Object.fromEntries(PATHFINDER_SAVES.map((save) => [save.id, 0]));
  dynamicSaveEffects.forEach(({ effect }) => {
    const ability = abilityList.find((entry) => entry.abilityId === effect.abilityId);
    const modifier = Math.floor((number(ability?.diceValue) - 10) / 2);
    const resolvedValue = effect.positiveOnly ? Math.max(0, modifier) : modifier;
    (effect.saves || []).forEach((saveId) => {
      classFeatureSaveBonuses[saveId] += resolvedValue;
    });
  });
  const manualBaseAttackModifier = Number.isFinite(Number(character.characteristics.manualBaseAttackModifier))
    ? Number(character.characteristics.manualBaseAttackModifier)
    : number(character.characteristics.baseAttackModifier);
  character.characteristics.manualBaseAttackModifier = manualBaseAttackModifier;
  character.characteristics.classBaseAttackBonus = classBaseAttackBonus;
  character.characteristics.baseAttackModifier = manualBaseAttackModifier + classBaseAttackBonus;
  character.characteristics.pathfinderSaves = PATHFINDER_SAVES.map((save) => {
    const existing = (character.characteristics.pathfinderSaves || []).find((entry) => entry.saveId === save.id) || {};
    const ability = abilityList.find((entry) => entry.abilityId === save.abilityId);
    const abilityModifier = Math.floor((number(ability?.diceValue) - 10) / 2);
    const manualModifier = Number.isFinite(Number(existing.manualModifier)) ? Number(existing.manualModifier) : number(existing.modifier);
    return {
      ...existing,
      saveId: save.id,
      abilityId: save.abilityId,
      manualModifier,
      classBaseBonus: baseSaveBonuses[save.id],
      classFeatureBonus: classFeatureSaveBonuses[save.id],
      racialBonus: racialSaveBonuses[save.id],
      modifier: abilityModifier + manualModifier + baseSaveBonuses[save.id] + classFeatureSaveBonuses[save.id] + racialSaveBonuses[save.id],
    };
  });
  character.skills.skillsList = (character.skills.skillsList || []).map((skill) => {
    const manualIsClassSkill = skill.manualIsClassSkill ?? skill.isClassSkill ?? false;
    const ruleClassSkill = classSkillIds.has(skill.catalogId);
    const playerSkillPoints = Math.max(
      0,
      Math.min(character.level.level, Math.trunc(number(skill.playerSkillPoints)))
    );
    return {
      ...skill,
      playerSkillPoints,
      manualIsClassSkill: Boolean(manualIsClassSkill),
      ruleClassSkill,
      isClassSkill: Boolean(manualIsClassSkill || ruleClassSkill),
      ruleAllowsUntrained: untrainedSkillGroups.has("knowledge") && String(skill.catalogId || "").startsWith("knowledge"),
      ruleBonus: number(skillBonuses[skill.catalogId])
        + (String(skill.catalogId || "").startsWith("knowledge") ? number(skillGroupBonuses.knowledge) : 0),
    };
  });
  const intelligence = abilityList.find((ability) => ability.abilityId === "intelligence");
  const intelligenceModifier = Math.floor((number(intelligence?.diceValue) - 10) / 2);
  const skillRankCapacity = character.characterClass.reduce((total, classEntry) => {
    const classRules = getClassRules(classEntry.id);
    if (!classRules) return total;
    const ranksPerLevel = Math.max(1, classRules.skillRanks + intelligenceModifier + skillRanksPerLevelBonus);
    return total + ranksPerLevel * classEntry.classLevel;
  }, 0);
  const manualFeatIds = new Set(character.feats.map((feat) => feat.catalogId || feat.id));
  featGrants.forEach((grantSources, featId) => {
    if (manualFeatIds.has(featId)) return;
    character.feats.push({
      id: `granted:${featId}`,
      catalogId: featId,
      generated: true,
      readOnly: true,
      grantSources,
    });
  });
  const spentSkillRanks = character.skills.skillsList.reduce(
    (total, skill) => total + Math.max(0, number(skill.playerSkillPoints)),
    0
  );
  const baseFeatCapacity = character.level.level > 0 ? Math.floor((character.level.level + 1) / 2) : 0;
  const featCapacity = baseFeatCapacity + bonusFeatCapacity;
  const spentFeats = Array.isArray(character.feats) ? character.feats.length : 0;

  character.grantedFeatures = grantedFeatures;
  character.ruleChoices = [...activeChoices, ...inactiveChoices];
  character.ruleDerived = {
    abilityAdjustments,
    armorProficiencies: [...armorProficiencies],
    baseSaveBonuses,
    classBaseAttackBonus,
    classFeatureSaveBonuses,
    classLevelMismatch: character.characterClass.length > 1 && classLevelTotal !== character.level.level,
    classLevelTotal,
    classSkillIds: [...classSkillIds],
    baseFeatCapacity,
    bonusFeatCapacity,
    hitDice,
    featCapacity,
    featMismatch: spentFeats !== featCapacity,
    racialSaveBonuses,
    skillBonuses,
    skillGroupBonuses,
    skillRankCapacity,
    skillRankMismatch: spentSkillRanks !== skillRankCapacity,
    skillRanksPerLevelBonus,
    spentFeats,
    spentSkillRanks,
    spellcasting,
    weaponProficiencies: [...weaponProficiencies],
    unresolvedChoiceIds: activeChoices.filter((choice) => choice.required && !choice.selectedValue && !choice.used).map((choice) => choice.id),
  };
  return character;
}

export default reconcileCharacterRules;
